import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { getReplies } from 'api/Comment';

import { cn, iaRa } from 'lib/helpers/utility';

import AvatarGroup from 'components/shared/AvatarGroup/AvatarGroup';
import Button from 'components/shared/Button/Button';
import { MYRIAD_STAFF_AVATAR_ID } from 'components/shared/ExpandableToolbar/lib/helpers';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import { useReplies } from '../useReplies';

import Comment from './Comment';
import CommentInput from './CommentInput';

const Replies = ({
  commentId,
  repliesCount: repliesCountProp,
  parentCreatedAt,
  category,
  refId,
  isExpanded,
  showReplyInput,
  onToggleExpanded,
  onToggleReplyInput
}) => {
  const navigate = useNavigate();
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);
  const isViewerAdmin = !!permission?.admin_id;
  const currentUserId = permission?.admin_id || userState?.id;

  const { data, isLoading } = useQuery(
    ['comment-replies', commentId],
    () => getReplies(navigate, { commentId }),
    {
      enabled: isExpanded,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 30000
    }
  );

  const { onReplyPostHandler, isCreatingReply, onReplyDeleteHandler, onReplySaveHandler } =
    useReplies(commentId, category, refId);

  const replies = iaRa(data?.replies);
  const repliesCount = replies.length || repliesCountProp;

  const uniqueAuthors = useMemo(() => {
    const toAvatarItem = (creator) => ({
      id: creator.id,
      f_name: creator.f_name,
      l_name: creator.l_name,
      image: creator.profile_photo
    });

    const seen = new Set();
    let hasOtherAdmin = false;

    const authors = replies.reduce((acc, reply) => {
      if (!reply.creator || seen.has(reply.creator.id)) return acc;
      seen.add(reply.creator.id);

      if (!isViewerAdmin && !reply.created_by_type && reply.creator.id !== currentUserId) {
        hasOtherAdmin = true;
        return acc;
      }

      acc.push(toAvatarItem(reply.creator));
      return acc;
    }, []);

    if (hasOtherAdmin) {
      authors.push({ id: MYRIAD_STAFF_AVATAR_ID, f_name: 'Myriad', l_name: 'Staff' });
    }

    return authors;
  }, [replies, isViewerAdmin, currentUserId]);

  const handleReplyPost = async (replyValue) => {
    await onReplyPostHandler(replyValue);
    onToggleReplyInput(false);
  };

  // Don't render anything if not expanded and no replies and no reply input
  if (!isExpanded && repliesCount === 0 && !showReplyInput) {
    return null;
  }

  return (
    <>
      {repliesCount > 0 && (
        <div className="flex items-center gap-1.5">
          {uniqueAuthors.length > 0 && <AvatarGroup items={uniqueAuthors} size="small" isCompact />}
          <Button
            size="small"
            variant="link"
            color="neutral"
            text={`${repliesCount} ${repliesCount === 1 ? 'reply' : 'replies'}`}
            icon={isExpanded ? 'chevron-up-linear' : 'chevron-down-linear'}
            onClick={onToggleExpanded}
            className="text-sm"
          />
        </div>
      )}

      {isExpanded && (
        <div className="pl-2">
          <div
            className={cn(
              'flex w-full flex-col gap-4 pl-3',
              !isLoading && repliesCount > 0 && 'border-0 border-l border-solid border-neutral-300'
            )}>
            {isLoading ? (
              <Typography color="neutral-500" size="small">
                Loading replies...
              </Typography>
            ) : (
              replies.map((reply) => (
                <Comment
                  isReply
                  key={reply.id}
                  comment={reply}
                  parentCreatedAt={parentCreatedAt}
                  category={category}
                  refId={refId}
                  onDelete={onReplyDeleteHandler}
                  onSave={onReplySaveHandler}
                />
              ))
            )}
          </div>
        </div>
      )}

      {showReplyInput && (
        <div className="w-full">
          <CommentInput
            focusOnMount
            onPost={handleReplyPost}
            loading={isCreatingReply}
            placeholder="Reply"
          />
        </div>
      )}
    </>
  );
};

export default Replies;
